import { RequestHeaders } from 'src/api';
import { action } from 'src/store/actions';
import EntityDescription, { EntityActions, Projection } from 'src/store/entities/types/EntityDescription';

export const FETCH = 'FETCH';
export const DELETE = 'DELETE';
export const NOT_MODIFIED = 'NOT_MODIFIED';
export const UPDATE = 'UPDATE';
export const CREATE = 'CREATE';
export const REQUEST = 'REQUEST';
export const SUCCESS_FETCH = 'SUCCESS_FETCH';
export const SUCCESS_DELETE = 'SUCCESS_DELETE';
export const FAILURE = 'FAILURE';

/**
 * Dispatches a {@link FETCH} action for the given entityDescription
 */
export const fetchEntities = (
    entityDescription: EntityDescription,
    projection?: Projection,
    searchMethod?: string,
    headers?: RequestHeaders
) => action(`${entityDescription.name}_${FETCH}`, { projection, searchMethod, headers });

/**
 * Dispatches a {@link FETCH} action for a single entity by id
 */
export const fetchEntity = (
    entityDescription: EntityDescription,
    id: string,
    projection?: Projection,
    headers?: RequestHeaders,
    /** true = always load the full entity, even when a (partial) copy already sits in the store */
    forceRefresh?: boolean
) =>
    action(`${entityDescription.name}_${FETCH}`, {
        projection,
        id,
        headers,
        forceRefresh,
    });

/**
 * Dispatches a {@link FETCH} action for a single entity by the given endpoint
 */
export const fetchEntityByEndpoint = (
    entityDescription: EntityDescription,
    endpoint: string,
    projection?: Projection
) => action(`${entityDescription.name}_${FETCH}`, { projection, endpoint });

/**
 * Dispatches a {@link CREATE} action for the given entity.
 */
export const createEntity = (entityDescription: EntityDescription, data: any, projection?: Projection) =>
    action(`${entityDescription.name}_${CREATE}`, { projection, data });

/**
 * Dispatches a {@link UPDATE} action for the given entity.
 */
export const patchEntity = (
    entityDescription: EntityDescription,
    data: any,
    projection?: Projection,
    patchMethod?: string
) => {
    if (!data.id) throw new Error('patchEntity(): Missing id of entity in data!');

    return action(`${entityDescription.name}_${UPDATE}`, { projection, data, patchMethod });
};

/**
 * Dispatches a {@link DELETE} action for the given entity.
 */
export const deleteEntity = (entityDescription: EntityDescription, id: number, projection?: Projection) =>
    action(`${entityDescription.name}_${DELETE}`, { id, projection });

/**
 * Enhances the given entityDescription with several actions for
 * request, success and failure cases in api actions
 */
export function createEntityActions(entityDescription: EntityDescription): EntityActions {
    return {
        request: (projection?: string) =>
            action(`${entityDescription.name}_${REQUEST}`, {
                projection,
            }),
        successFetch: (response: any, projection?: string, mergeItems?: boolean) =>
            action(`${entityDescription.name}_${SUCCESS_FETCH}`, {
                entityDescription,
                projection,
                response,
                mergeItems,
            }),
        notModified: (projection?: string) =>
            action(`${entityDescription.name}_${NOT_MODIFIED}`, {
                entityDescription,
                projection,
            }),
        successDelete: (response: any, projection?: string) =>
            action(`${entityDescription.name}_${SUCCESS_DELETE}`, {
                entityDescription,
                projection,
                response,
            }),
        failure: (error: any, projection?: string) =>
            action(`${entityDescription.name}_${FAILURE}`, {
                error,
                projection,
            }),
    };
}
